import { useState } from "react"
import { cn } from "@/common/style/classMerge"
import { Button } from "@/common/elements/button"
import { Input } from "@/common/elements/input"
import { MailIcon } from "lucide-react"
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/common/elements/field"

export function LoginForm({
  onLogin,
  isLoading,
  errorMessage,
  className,
  ...props
}: React.ComponentProps<"form"> & {
  onLogin: (email: string) => void | Promise<void>
  isLoading?: boolean
  errorMessage?: string | null
}) {
  const [email, setEmail] = useState("")

  return (
    <form
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border/70 bg-background/95 p-6 shadow-[0_28px_70px_-40px_hsl(var(--foreground)/0.55)] backdrop-blur-sm sm:p-7",
        className
      )}
      onSubmit={(event) => {
        event.preventDefault()
        const normalizedEmail = email.trim()
        onLogin(normalizedEmail)
      }}
      {...props}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,var(--chart-1),var(--chart-3),var(--chart-5))]" />
      <FieldGroup className="gap-6">
        <div className="space-y-2 text-center">
          <p className="text-xs font-semibold tracking-[0.22em] text-muted-foreground uppercase">
            Secure Sign In
          </p>
          <h1 className="text-2xl font-semibold text-foreground sm:text-[1.75rem]">CloudFit Portal</h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and you will be redirected to your organization's secure provider.
          </p>
        </div>
        <Field>
          <FieldLabel htmlFor="email" className="text-sm font-medium text-foreground">
            Email address
          </FieldLabel>
          <div className="relative">
            <MailIcon
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="email"
              name="email"
              placeholder="you@company.com"
              autoComplete="email"
              className="h-10 bg-background pl-9"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
        </Field>
        {errorMessage ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}
        <Field>
          <Button type="submit" size="lg" className="w-full text-sm font-semibold" disabled={isLoading}>
            {isLoading ? "Redirecting..." : "Continue"}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  )
}
